import { HttpClient, HttpParams } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { MatPaginatorIntl, MatPaginatorModule, type PageEvent } from '@angular/material/paginator';
import {
  AlertComponent,
  BadgeComponent,
  ButtonComponent,
  IconComponent,
  type IconName,
  SearchComponent,
  SelectComponent,
  SkeletonComponent,
} from 'nebula-ui-kit';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import {
  userIsSuperadminFromSession,
  VaultAppToastService,
  emailIsCanonicalVaultSuperadmin,
  formatVaultLocalDate,
  formatVaultLocalDateTime,
  formatVaultLocalTime,
  parseVaultApiDateTime,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
} from '@nebula/shared-vault/data-access';
import { AuditService } from '../../../../data-access/src/lib/audit/audit.service';
import { extractAdminUsersArray, normalizeAdminSystemUser } from '../../../../data-access/src/lib/admin-users/admin-user-normalize.util';
import { formatAdminUserCompanySummary, resolveAdminUserSuperadminForUi } from '../admin-users/admin-users.utils';
import {
  resolveTenantId,
  tenantHeaders,
} from '../../../../data-access/src/lib/shared/tenant-context.util';
import type { AuditLogResponse } from '../../../../domain/src/lib/audit/audit.model';
import { ServerContextService } from '../servers/server-context.service';
import { auditActionDisplayLabel, auditEntityTypeFilterLabel, auditModuleLabel, auditResourceId } from './audit-display.util';
import { createSpanishMatPaginatorIntl } from './audit-paginator-intl.util';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  ExportFormatOption,
  ExportFormatSelectorComponent,
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  resolveModuleInitialLoadError,
  VaultModuleShellComponent,
} from '@nebula-shared/ui';

/** Etiquetas en español para claves habituales del API de auditoría. */
const AUDIT_DETAIL_KEY_LABELS: Record<string, string> = {
  action: 'Acción',
  closed_at: 'Fecha de cierre',
  created_at: 'Fecha de creación',
  updated_at: 'Fecha de actualización',
  is_active: 'Activo',
  status: 'Estado',
  reason: 'Motivo',
  notes: 'Notas',
  name: 'Nombre',
  title: 'Título',
  description: 'Descripción',
  entity_id: 'ID del recurso',
  project_id: 'ID de expediente',
  project_name: 'Nombre del expediente',
  upload_id: 'ID de carga',
  filename: 'Archivo',
  folder_path: 'Ruta de carpeta',
  subfolder_path: 'Ruta de subcarpeta',
  actor_email: 'Correo del usuario',
  actor_full_name: 'Nombre del usuario',
  user_id: 'ID de usuario',
  tenant_id: 'ID de tenant',
  policy_id: 'ID de política',
  legal_hold_id: 'ID de retención legal',
  retention_policy_id: 'ID de política de retención',
  metadata: 'Metadatos',
  changes: 'Cambios',
  before: 'Valor anterior',
  after: 'Valor nuevo',
  payload: 'Datos',
};

interface TenantOption {
  label: string;
  value: string;
}

interface AdminTenantSummary {
  id?: string;
  name?: string;
}

@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrls: ['./audit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    SelectComponent,
    ButtonComponent,
    BadgeComponent,
    IconComponent,
    SearchComponent,
    SkeletonComponent,
    AlertComponent,
    VaultModuleShellComponent,
    PageHeaderComponent,
    ExportFormatSelectorComponent,
    MatPaginatorModule,
  ],
  providers: [{ provide: MatPaginatorIntl, useFactory: createSpanishMatPaginatorIntl }],
})
export class AuditComponent {
  private readonly auditService = inject(AuditService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly vaultAppToast = inject(VaultAppToastService);

  private static readonly MODULE_LABEL = 'Registro de actividad';
  private static readonly LIMIT = 500;
  /** Texto plano en exportaciones CSV/Excel (sin iconos). */
  private static readonly SUPERADMIN_EXPORT_LABEL = 'Superadmin (acceso total)';
  readonly superadminScopeTooltip =
    'Superadministrador con acceso total al sistema. No tiene empresa asignada.';

  readonly isSuperAdmin = computed(() => userIsSuperadminFromSession());
  readonly loading = signal(false);
  readonly accessDenied = signal(false);
  /** Error de listado en línea: no oculta la tabla vía vault-module-shell. */
  readonly listError = signal<string | null>(null);
  readonly exporting = signal(false);
  readonly rawRows = signal<AuditLogResponse[]>([]);
  readonly expandedRowIds = signal<Record<string, boolean>>({});

  readonly pageIndex = signal(0);
  readonly pageSize = createVaultTablePageSizeSignal();
  readonly pageSizeOptions: readonly number[] = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];
  readonly limitLabel = String(AuditComponent.LIMIT);
  readonly auditModuleLabel = auditModuleLabel;

  readonly searchTerm = signal('');
  readonly entityTypeFilter = signal('');
  readonly actionFilter = signal('');
  readonly selectedTenantId = signal('');
  readonly selectedUserId = signal('');

  /** Usuarios vistos en cargas previas; evita que el select se vacíe al filtrar. */
  private readonly knownUsers = signal<Map<string, string>>(new Map());
  /** Catálogo id → nombre para enriquecer empresa cuando el API no envía `tenant`. */
  private readonly tenantCatalog = signal<Array<{ id: string; name: string }>>([]);
  /** Empresa asignada por usuario (desde GET /api/admin/users). */
  private readonly userCompanyByUserId = signal<Map<string, string>>(new Map());
  /** Usuarios superadmin sin empresa Vault (acceso global). */
  private readonly superadminUserIds = signal<Set<string>>(new Set());

  readonly tenantOptions = signal<TenantOption[]>([{ label: 'Todas las empresas', value: '' }]);
  readonly entityTypeOptions = signal<Array<{ label: string; value: string }>>([
    { label: 'Todos los módulos', value: '' },
  ]);

  readonly exportOptions: ExportFormatOption[] = [
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'excel', label: 'Excel (.xls)' },
  ];

  readonly actionOptions = [
    { label: 'Todas las acciones', value: '' },
    { label: 'Crear', value: 'CREATE' },
    { label: 'Actualizar', value: 'UPDATE' },
    { label: 'Eliminar', value: 'DELETE' },
    { label: 'Subir archivos', value: 'UPLOAD' },
    { label: 'Inicio de sesión', value: 'LOGIN' },
    { label: 'Cierre de sesión', value: 'LOGOUT' },
  ];

  readonly headerSubtitle = computed(() =>
    this.isSuperAdmin()
      ? 'Registro completo de todas las actividades del sistema'
      : 'Registro de actividades de usuarios de tu empresa',
  );

  readonly usesGlobalApi = computed(
    () => this.isSuperAdmin() && !this.selectedTenantId().trim(),
  );

  readonly userFilterOptions = computed(() => {
    const options: Array<{ label: string; value: string }> = [
      { label: 'Todos los usuarios', value: '' },
    ];
    const entries = [...this.knownUsers().entries()].sort((a, b) =>
      a[1].localeCompare(b[1], 'es'),
    );
    for (const [value, label] of entries) {
      options.push({ label, value });
    }
    return options;
  });

  readonly filteredLogs = computed(() => {
    let rows = this.rawRows();

    const entity = this.entityTypeFilter().trim();
    if (entity) {
      rows = rows.filter((r) => (r.entity_type ?? '').trim() === entity);
    }

    const userId = this.selectedUserId().trim();
    if (userId) {
      rows = rows.filter((r) => this.matchesSelectedUser(r, userId));
    }

    const action = this.actionFilter().trim();
    if (action) {
      rows = rows.filter((r) => this.matchesActionType(r.action, action));
    }

    const q = this.searchTerm().trim();
    if (!q) return rows;

    return rows.filter((log) => this.matchesSearchTerm(log, q));
  });

  readonly totalEvents = computed(() => this.filteredLogs().length);

  readonly paginatedLogs = computed(() => {
    const rows = this.filteredLogs();
    const size = this.pageSize();
    const start = this.pageIndex() * size;
    return rows.slice(start, start + size);
  });
  readonly creates = computed(() =>
    this.filteredLogs().filter((row) => this.matchesActionType(row.action, 'CREATE')).length,
  );
  readonly updates = computed(() =>
    this.filteredLogs().filter((row) => this.matchesActionType(row.action, 'UPDATE')).length,
  );
  readonly deletes = computed(() =>
    this.filteredLogs().filter((row) => this.matchesActionType(row.action, 'DELETE')).length,
  );

  readonly showInitialSkeleton = computed(() => this.loading() && this.rawRows().length === 0);
  readonly showTable = computed(() => !this.showInitialSkeleton());

  constructor() {
    bindVaultTablePageSizeToPreferences(this.pageSize, this.destroyRef);
    if (this.isSuperAdmin()) {
      this.loadTenants();
    }
    this.reload();
    this.loadUserFilterDirectory();

    effect(() => {
      const total = this.filteredLogs().length;
      const size = Math.max(1, this.pageSize());
      const maxIndex = Math.max(0, Math.ceil(total / size) - 1);
      if (this.pageIndex() > maxIndex) {
        this.pageIndex.set(maxIndex);
      }
    });
  }

  onPageChange(ev: PageEvent): void {
    this.pageIndex.set(ev.pageIndex);
    this.pageSize.set(ev.pageSize);
  }

  reload(): void {
    this.loading.set(true);
    this.listError.set(null);

    const isSuper = this.isSuperAdmin();
    const tenantId = this.selectedTenantId().trim();
    const listParams = this.buildTenantListParams();

    let request$;
    if (isSuper && !tenantId) {
      request$ = this.auditService.listGlobal({
        action: this.actionFilter().trim() || undefined,
        limit: AuditComponent.LIMIT,
        offset: 0,
      });
    } else if (isSuper && tenantId) {
      request$ = this.auditService.list(listParams, { tenantId });
    } else {
      request$ = this.auditService.list(listParams);
    }

    request$.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (rows) => {
        const list = rows ?? [];
        this.rawRows.set(list);
        this.mergeKnownUsers(list);
        this.syncEntityTypeOptions(list);
        this.expandedRowIds.set({});
        this.pageIndex.set(0);
        this.loadUserFilterDirectory();
      },
      error: (err: unknown) => this.handleListError(err),
    });
  }

  applyServerFilters(): void {
    this.reload();
  }

  onEntityTypeChange(value: string): void {
    this.entityTypeFilter.set(value ?? '');
    this.pageIndex.set(0);
    if (!this.usesGlobalApi()) {
      this.reload();
    }
  }

  onActionChange(value: string): void {
    this.actionFilter.set(value ?? '');
    this.pageIndex.set(0);
    if (this.usesGlobalApi()) {
      this.reload();
    }
  }

  onTenantChange(value: string): void {
    this.selectedTenantId.set(value ?? '');
    this.selectedUserId.set('');
    this.knownUsers.set(new Map());
    this.entityTypeOptions.set([{ label: 'Todos los módulos', value: '' }]);
    this.pageIndex.set(0);
    this.reload();
    this.loadUserFilterDirectory();
  }

  onUserFilterChange(value: string): void {
    this.selectedUserId.set(value ?? '');
    this.pageIndex.set(0);
    if (!this.usesGlobalApi()) {
      this.reload();
    }
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value ?? '');
    this.pageIndex.set(0);
  }

  clearClientFilters(): void {
    this.searchTerm.set('');
    this.entityTypeFilter.set('');
    this.actionFilter.set('');
    this.selectedUserId.set('');
    this.pageIndex.set(0);
    this.reload();
  }

  onExportFormatSelected(format: string): void {
    const rows = this.filteredLogs();
    if (rows.length === 0) {
      this.vaultAppToast.show({
        type: 'warning',
        title: 'Sin datos',
        message: 'No hay registros para exportar con los filtros actuales.',
      });
      return;
    }

    this.exporting.set(true);
    try {
      if (format === 'excel') {
        this.exportExcel(rows);
      } else {
        this.exportCsv(rows);
      }
      this.vaultAppToast.show({
        type: 'success',
        title: 'Éxito',
        message: `Se exportaron ${rows.length} registro(s) de actividad.`,
      });
    } catch {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Error',
        message: 'No se pudo generar el archivo de exportación.',
      });
    } finally {
      this.exporting.set(false);
    }
  }

  private exportCsv(rows: AuditLogResponse[]): void {
    const includeTenant = this.isSuperAdmin();
    const headers = [
      'Fecha y hora',
      'Usuario',
      'Email',
      'Acción',
      'Módulo',
      'ID del recurso',
      ...(includeTenant ? ['Empresa'] : []),
      'IP',
      'Detalles',
      'ID del registro',
    ];

    const lines = [headers.map((h) => this.csvCell(h)).join(';')];
    for (const log of rows) {
      const cells = [
        this.formatFullDate(log.created_at),
        this.userDisplayName(log),
        this.userDisplayEmail(log),
        this.actionBadgeLabel(log),
        auditModuleLabel(log.entity_type),
        this.exportResourceId(log),
        ...(includeTenant ? [this.auditTenantExportLabel(log)] : []),
        log.ip_address ?? '',
        this.formatAuditDetails(log.details).replace(/\n/g, ' | '),
        log.id,
      ];
      lines.push(cells.map((c) => this.csvCell(c)).join(';'));
    }

    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, this.exportFileName('csv'));
  }

  private exportExcel(rows: AuditLogResponse[]): void {
    const includeTenant = this.isSuperAdmin();
    const headerCells = [
      'Fecha y hora',
      'Usuario',
      'Email',
      'Acción',
      'Módulo',
      'ID del recurso',
      ...(includeTenant ? ['Empresa'] : []),
      'IP',
      'Detalles',
      'ID del registro',
    ];

    const bodyRows = rows.map((log) => {
      const cells = [
        this.escapeHtml(this.formatFullDate(log.created_at)),
        this.escapeHtml(this.userDisplayName(log)),
        this.escapeHtml(this.userDisplayEmail(log)),
        this.escapeHtml(this.actionBadgeLabel(log)),
        this.escapeHtml(auditModuleLabel(log.entity_type)),
        this.escapeHtml(this.exportResourceId(log)),
        ...(includeTenant ? [this.escapeHtml(this.auditTenantExportLabel(log))] : []),
        this.escapeHtml(log.ip_address ?? ''),
        this.escapeHtml(this.formatAuditDetails(log.details).replace(/\n/g, ' | ')),
        this.escapeHtml(log.id),
      ];
      return `<tr>${cells.map((c) => `<td>${c}</td>`).join('')}</tr>`;
    });

    const htmlDoc = [
      '<html><head><meta charset="UTF-8"></head><body>',
      '<table border="1">',
      `<tr>${headerCells.map((h) => `<th>${this.escapeHtml(h)}</th>`).join('')}</tr>`,
      ...bodyRows,
      '</table>',
      '</body></html>',
    ].join('');

    const blob = new Blob([`\uFEFF${htmlDoc}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    this.downloadBlob(blob, this.exportFileName('xls'));
  }

  private exportFileName(extension: string): string {
    return `registro-actividad-${new Date().toISOString().slice(0, 10)}.${extension}`;
  }

  private csvCell(value: string): string {
    return `"${value.replaceAll('"', '""')}"`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
    saveAs(file);
  }

  toggleRowExpansion(logId: string): void {
    this.expandedRowIds.update((prev) => ({ ...prev, [logId]: !prev[logId] }));
  }

  isRowExpanded(logId: string): boolean {
    return !!this.expandedRowIds()[logId];
  }

  formatRelativeTime(date: string): string {
    const now = Date.now();
    const logDate = parseVaultApiDateTime(date).getTime();
    if (Number.isNaN(logDate)) return date;
    const diffInSeconds = Math.floor((now - logDate) / 1000);
    if (diffInSeconds < 60) return 'Hace unos segundos';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `Hace ${days} día${days > 1 ? 's' : ''}`;
    }
    return formatVaultLocalDate(date);
  }

  formatDatePart(date: string): string {
    return formatVaultLocalDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatTimePart(date: string): string {
    return formatVaultLocalTime(date);
  }

  formatFullDate(date: string): string {
    return formatVaultLocalDateTime(date);
  }

  userDisplayName(log: AuditLogResponse): string {
    return log.user?.full_name?.trim() || log.user?.username?.trim() || '—';
  }

  userDisplayEmail(log: AuditLogResponse): string {
    return log.user?.email?.trim() || log.user_id || '—';
  }

  /** Empresa del registro: API → tenant_id + catálogo → asignación del usuario en admin. */
  auditTenantLabel(log: AuditLogResponse): string {
    const fromApi = log.tenant?.name?.trim();
    if (fromApi) return fromApi;

    const tenantId = (log.tenant_id ?? log.tenant?.id ?? '').trim();
    if (tenantId) {
      const fromCatalog = this.tenantCatalog().find((t) => t.id === tenantId)?.name?.trim();
      if (fromCatalog) return fromCatalog;
    }

    const userId = (log.user_id ?? log.user?.id ?? '').trim();
    if (userId) {
      const fromUser = this.userCompanyByUserId().get(userId)?.trim();
      if (fromUser && fromUser !== '—') return fromUser;
    }

    return '—';
  }

  /** Etiqueta de empresa para exportación (incluye texto para superadmin). */
  auditTenantExportLabel(log: AuditLogResponse): string {
    if (this.isAuditLogSuperadminScope(log)) {
      return AuditComponent.SUPERADMIN_EXPORT_LABEL;
    }
    const label = this.auditTenantLabel(log);
    return label === '—' ? '' : label;
  }

  isAuditLogSuperadminScope(log: AuditLogResponse): boolean {
    if (emailIsCanonicalVaultSuperadmin(log.user?.email)) return true;
    const userId = (log.user_id ?? log.user?.id ?? '').trim();
    return userId.length > 0 && this.superadminUserIds().has(userId);
  }

  isLoginAction(action: string | null | undefined): boolean {
    return (action ?? '').toUpperCase().includes('LOGIN');
  }

  actionIconName(action: string): IconName {
    const actionUpper = action.toUpperCase();
    if (actionUpper.includes('UPLOAD') || actionUpper.includes('SUBIR')) return 'Upload';
    if (actionUpper.includes('CREATE') || actionUpper.includes('CREAR')) return 'CircleCheck';
    if (actionUpper.includes('UPDATE') || actionUpper.includes('ACTUALIZAR')) return 'Info';
    if (actionUpper.includes('DELETE') || actionUpper.includes('ELIMINAR')) return 'CircleX';
    if (actionUpper.includes('LOGIN')) return 'CircleCheck';
    if (actionUpper.includes('LOGOUT')) return 'CircleX';
    return 'CircleAlert';
  }

  actionIconClass(action: string): string {
    const actionUpper = action.toUpperCase();
    if (actionUpper.includes('UPLOAD') || actionUpper.includes('SUBIR')) {
      return 'audit-table__action-icon is-create';
    }
    if (actionUpper.includes('CREATE') || actionUpper.includes('CREAR') || actionUpper.includes('LOGIN')) {
      return 'audit-table__action-icon is-create';
    }
    if (actionUpper.includes('UPDATE') || actionUpper.includes('ACTUALIZAR')) {
      return 'audit-table__action-icon is-update';
    }
    if (actionUpper.includes('DELETE') || actionUpper.includes('ELIMINAR')) {
      return 'audit-table__action-icon is-delete';
    }
    if (actionUpper.includes('LOGOUT')) return 'audit-table__action-icon is-logout';
    return 'audit-table__action-icon is-default';
  }

  actionBadgeWrapClass(action: string): string {
    return action.toUpperCase().includes('LOGOUT') ? 'audit-table__badge-outline' : '';
  }

  actionBadgeVariant(action: string): 'success' | 'info' | 'warning' | 'neutral' | 'error' {
    const actionUpper = action.toUpperCase();
    if (
      actionUpper.includes('CREATE') ||
      actionUpper.includes('CREAR') ||
      actionUpper.includes('LOGIN') ||
      actionUpper.includes('UPLOAD') ||
      actionUpper.includes('SUBIR')
    ) {
      return 'success';
    }
    if (actionUpper.includes('UPDATE') || actionUpper.includes('ACTUALIZAR')) {
      return 'info';
    }
    if (actionUpper.includes('DELETE') || actionUpper.includes('ELIMINAR')) {
      return 'error';
    }
    if (actionUpper.includes('LOGOUT')) return 'neutral';
    return 'neutral';
  }

  actionBadgeLabel(log: AuditLogResponse): string {
    return auditActionDisplayLabel(log.action, log.entity_type);
  }

  formatAuditDetails(details: Record<string, unknown> | null | undefined): string {
    if (!details) return '—';
    const entries = Object.entries(details).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '—';
    return entries
      .map(([k, v]) => `${this.humanizeDetailKey(k)}: ${this.formatDetailValue(v, k)}`)
      .join('\n');
  }

  hasDetails(log: AuditLogResponse): boolean {
    return !!log.details;
  }

  hasExpandedDetails(log: AuditLogResponse): boolean {
    return !!log.details && Object.keys(log.details).length > 0;
  }

  tableColspan(): number {
    return this.isSuperAdmin() ? 8 : 7;
  }

  auditResourceIdFor(log: AuditLogResponse): string {
    return auditResourceId(log.entity_id, log.details);
  }

  private exportResourceId(log: AuditLogResponse): string {
    return this.auditResourceIdFor(log);
  }

  private handleListError(err: unknown): void {
    const resolved = resolveModuleInitialLoadError(err, {
      moduleLabel: AuditComponent.MODULE_LABEL,
      fallback:
        'No se pudieron cargar los registros de actividad. Comprobá la conexión con el servidor.',
    });
    if (resolved.accessDenied) {
      this.accessDenied.set(true);
      return;
    }
    this.listError.set(resolved.message);
    this.vaultAppToast.show({
      type: 'error',
      title: resolved.title,
      message: resolved.message,
    });
  }

  private loadTenants(): void {
    this.http.get<AdminTenantSummary[] | { data?: AdminTenantSummary[] }>('/api/admin/tenants').subscribe({
      next: (raw) => {
        const tenants = Array.isArray(raw) ? raw : (raw?.data ?? []);
        const catalog: Array<{ id: string; name: string }> = [];
        const options: TenantOption[] = [{ label: 'Todas las empresas', value: '' }];
        for (const t of tenants) {
          if (t.id && t.name) {
            options.push({ label: t.name, value: t.id });
            catalog.push({ id: t.id, name: t.name });
          }
        }
        this.tenantOptions.set(options);
        this.tenantCatalog.set(catalog);
        if (this.isSuperAdmin() && this.knownUsers().size > 1) {
          this.loadUserFilterDirectory();
        }
      },
      error: () => {
        /* Sin lista de empresas: el filtro queda solo con "Todas". */
      },
    });
  }

  private syncEntityTypeOptions(rows: AuditLogResponse[]): void {
    const types = new Set(
      this.entityTypeOptions()
        .map((option) => option.value.trim())
        .filter((value) => value.length > 0),
    );
    for (const row of rows) {
      const type = (row.entity_type ?? '').trim();
      if (type) types.add(type);
    }
    const sorted = [...types].sort((a, b) =>
      auditEntityTypeFilterLabel(a).localeCompare(auditEntityTypeFilterLabel(b), 'es'),
    );
    this.entityTypeOptions.set([
      { label: 'Todos los módulos', value: '' },
      ...sorted.map((type) => ({ label: auditEntityTypeFilterLabel(type), value: type })),
    ]);
  }

  private mergeKnownUsers(rows: AuditLogResponse[]): void {
    this.knownUsers.update((prev) => {
      const next = new Map(prev);
      for (const row of rows) {
        const option = this.auditUserOption(row);
        if (!option) continue;
        next.set(option.value, option.label);
      }
      return next;
    });
  }

  private buildTenantListParams(): {
    entity_type?: string;
    user_id?: string;
    limit: number;
    offset: number;
  } {
    const params: {
      entity_type?: string;
      user_id?: string;
      limit: number;
      offset: number;
    } = {
      limit: AuditComponent.LIMIT,
      offset: 0,
    };
    const entityType = this.entityTypeFilter().trim();
    if (entityType) params.entity_type = entityType;
    const userId = this.selectedUserId().trim();
    if (userId && this.isUuid(userId)) params.user_id = userId;
    return params;
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value.trim(),
    );
  }

  private matchesSelectedUser(row: AuditLogResponse, selected: string): boolean {
    const token = selected.trim().toLowerCase();
    const rowUserId = String(row.user_id ?? '')
      .trim()
      .toLowerCase();
    const rowEmail = (row.user?.email ?? '').trim().toLowerCase();
    return rowUserId === token || rowEmail === token;
  }

  private auditUserOption(row: AuditLogResponse): { value: string; label: string } | null {
    const id = (row.user_id ?? '').trim();
    const name = row.user?.full_name?.trim() || row.user?.username?.trim();
    const email = row.user?.email?.trim();
    const value = id || email || '';
    if (!value) return null;
    const label = name && email ? `${name} (${email})` : name || email || value;
    return { value, label };
  }

  private loadUserFilterDirectory(): void {
    const tenantId = this.resolveActiveTenantId();
    let params = new HttpParams();
    const selectedTenant = this.selectedTenantId().trim();
    if (this.isSuperAdmin() && selectedTenant) {
      params = params.set('tenant_id', selectedTenant);
    }

    this.http
      .get<unknown>('/api/admin/users', {
        headers: tenantHeaders(tenantId),
        ...(params.keys().length > 0 ? { params } : {}),
      })
      .pipe(catchError(() => of(null)))
      .subscribe((raw) => {
        if (!raw) {
          this.supplementUsersFromAuditHistory();
          return;
        }

        const items = extractAdminUsersArray(raw);
        const catalog = this.tenantCatalog();
        this.knownUsers.update((prev) => {
          const next = new Map(prev);
          const companyMap = new Map(this.userCompanyByUserId());
          const superadminIds = new Set(this.superadminUserIds());
          for (const item of items) {
            const user = normalizeAdminSystemUser(item);
            const id = user.id.trim();
            if (!id) continue;
            const name = user.full_name.trim();
            const email = user.email.trim();
            const label = name && email ? `${name} (${email})` : name || email || id;
            next.set(id, label);
            if (user.is_superadmin || resolveAdminUserSuperadminForUi(user)) {
              superadminIds.add(id);
              companyMap.delete(id);
            } else {
              superadminIds.delete(id);
              const company = formatAdminUserCompanySummary(user, catalog);
              if (company !== '—') {
                companyMap.set(id, company);
              } else {
                companyMap.delete(id);
              }
            }
          }
          this.userCompanyByUserId.set(companyMap);
          this.superadminUserIds.set(superadminIds);
          return next;
        });

        if (this.knownUsers().size <= 1) {
          this.supplementUsersFromAuditHistory();
        }
      });
  }

  /** Para roles sin acceso a /api/admin/users (p. ej. auditor): recorre páginas del historial. */
  private supplementUsersFromAuditHistory(): void {
    const offsets = [100, 200, 300, 400];
    const selectedTenant = this.selectedTenantId().trim();
    const isSuper = this.isSuperAdmin();

    const requests = offsets.map((offset) => {
      if (isSuper && !selectedTenant) {
        return this.auditService.listGlobal({ limit: 100, offset });
      }
      if (isSuper && selectedTenant) {
        return this.auditService.list({ limit: 100, offset }, { tenantId: selectedTenant });
      }
      return this.auditService.list({ limit: 100, offset });
    });

    forkJoin(requests)
      .pipe(catchError(() => of([] as AuditLogResponse[][])))
      .subscribe((pages) => {
        for (const rows of pages) {
          if (Array.isArray(rows)) {
            this.mergeKnownUsers(rows);
          }
        }
      });
  }

  private resolveActiveTenantId(): string {
    const selectedTenant = this.selectedTenantId().trim();
    if (selectedTenant) return selectedTenant;
    return resolveTenantId(this.serverContext);
  }

  private matchesSearchTerm(log: AuditLogResponse, query: string): boolean {
    const q = query.toLowerCase();
    const parts = [
      log.action,
      this.actionBadgeLabel(log),
      log.entity_type,
      auditModuleLabel(log.entity_type),
      auditEntityTypeFilterLabel(log.entity_type),
      log.entity_id,
      this.auditResourceIdFor(log),
      log.details?.['filename'],
      log.details?.['project_id'],
      log.details?.['upload_id'],
      log.details?.['folder_path'],
      log.details?.['subfolder_path'],
      log.details?.['name'],
      log.user?.email,
      log.user?.full_name,
      log.user?.username,
      log.user_id,
      log.ip_address,
      log.tenant?.name,
      this.auditTenantLabel(log),
      this.auditTenantExportLabel(log),
      log.created_at,
      this.formatDatePart(log.created_at),
      this.formatTimePart(log.created_at),
      this.formatFullDate(log.created_at),
      this.formatRelativeTime(log.created_at),
      this.formatAuditDetails(log.details),
    ];
    return parts
      .filter((part) => part != null && String(part).trim().length > 0)
      .some((part) => String(part).toLowerCase().includes(q));
  }

  private matchesActionType(action: string | null | undefined, filter: string): boolean {
    if (!filter) return true;
    const value = (action ?? '').toUpperCase();
    const token = filter.toUpperCase();
    if (token === 'CREATE') return value.includes('CREATE') || value.includes('CREAR');
    if (token === 'UPDATE') return value.includes('UPDATE') || value.includes('ACTUALIZAR');
    if (token === 'DELETE') return value.includes('DELETE') || value.includes('ELIMINAR');
    if (token === 'UPLOAD') return value.includes('UPLOAD') || value.includes('SUBIR');
    return value.includes(token);
  }

  private humanizeDetailKey(key: string): string {
    const mapped = AUDIT_DETAIL_KEY_LABELS[key];
    if (mapped) return mapped;
    return key
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatDetailValue(value: unknown, key?: string): string {
    if (value === null || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'string') {
      if (key === 'action' || key === 'status') {
        const t = this.humanizeDetailToken(value);
        if (t) return t;
      }
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return formatVaultLocalDateTime(value);
      }
      return value;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      return value.map((item) => this.formatDetailValue(item, key)).join(', ');
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 0);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  private humanizeDetailToken(raw: string): string | null {
    const s = raw.trim().toLowerCase();
    const map: Record<string, string> = {
      close: 'Cerrar',
      closed: 'Cerrado',
      open: 'Abrir',
      opened: 'Abierto',
      active: 'Activo',
      inactive: 'Inactivo',
      pending: 'Pendiente',
      completed: 'Completado',
      cancelled: 'Cancelado',
      create: 'Crear',
      update: 'Actualizar',
      delete: 'Eliminar',
    };
    return map[s] ?? null;
  }
}
